
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { getSuggestedCandidates } from "@/services/candidateMatchingService";
import type { Job } from "@/components/jobs/types";
import type { SuggestedCandidate } from "@/types/candidateSuggestion";

const ITEMS_PER_PAGE = 5;

export const useMatchingCandidatesLogic = (job: Job) => {
  const { user } = useAuth();
  const [minMatchScore, setMinMatchScore] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { 
    data: suggestedCandidates, 
    isLoading, 
    error 
  } = useQuery<SuggestedCandidate[], Error>({
    queryKey: ["suggestedCandidates", job.id, user?.id],
    queryFn: async () => {
      if (!user?.id) {
        // This case should ideally be handled by `enabled` option, 
        // but defensive check is fine.
        // Or, let it throw, and the component handles the error state.
        // For consistency with original, we'll let `enabled` handle it primarily.
        return []; 
      }

      const jobForService = {
        id: job.id,
        title: job.title, 
        skills: job.skills ?? [], 
        experience_required: job.experience_required ?? null,
        location: job.location ?? null,
        posted: job.posted, 
        company: job.company ?? undefined,
        description: job.description ?? undefined,
      };

      if (jobForService.skills.length === 0) {
        console.warn(`MatchingCandidatesLogic: Job with ID ${job.id} has no skills listed. Skill matching might be affected.`);
      }
      if (jobForService.experience_required === null) {
        console.warn(`MatchingCandidatesLogic: Job with ID ${job.id} has no experience_required specified. Experience matching might be affected.`);
      }
      if (jobForService.location === null) {
        console.warn(`MatchingCandidatesLogic: Job with ID ${job.id} has no location specified. Location matching might be affected.`);
      }
      
      return getSuggestedCandidates(jobForService, user.id);
    },
    enabled: !!user?.id && !!job?.id,
  });

  const filteredCandidates = useMemo(() => {
    if (!suggestedCandidates) return [];
    return suggestedCandidates.filter(candidate => candidate.match_score >= minMatchScore);
  }, [suggestedCandidates, minMatchScore]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE));
  }, [filteredCandidates]);

  useEffect(() => {
    if (currentPage > totalPages) {
        setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedCandidates = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredCandidates.slice(startIndex, endIndex);
  }, [filteredCandidates, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleMinMatchScoreChange = (score: number) => {
    setMinMatchScore(score);
    setCurrentPage(1); 
  };

  const noResultsAfterFilter = filteredCandidates.length === 0 && (suggestedCandidates?.length ?? 0) > 0;
  const noInitialSuggestions = !suggestedCandidates || suggestedCandidates.length === 0;

  return {
    user,
    isLoading,
    error,
    minMatchScore,
    handleMinMatchScoreChange,
    paginatedCandidates,
    currentPage,
    totalPages,
    handlePageChange,
    noResultsAfterFilter,
    noInitialSuggestions,
    suggestedCandidates // Exporting this to determine initial suggestions state accurately
  };
};
